import { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isValid, parseISO } from 'date-fns';
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { capitalizeFirstLetter, formatTZDate } from 'state/helpers';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import FlexCenter from 'components/FlexCenter';
import DeleteConfirm from 'components/DeleteConfirm';
import CrewKPIDataList from './CrewKPIDataList';


const CrewKPILogs = ({ rows, onDelete }) => {
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canDelete = hasRight(permissionMap, 'CrewKpis', 'delete', false);

  const [deleteType, setDeleteType] = useState('single'); // 'single' or 'bulk'
  const [deleteId, setDeleteId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  const formatGeneralValue = useCallback((value, indent = 0) => {
    const spacing = " ".repeat(indent * 2);
    if (value == null) return "";

    // Date        
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)) {
      const date = parseISO(value);
      if (isValid(date)) {
        if (value.endsWith("T00:00:00.000Z")) {
          return formatTZDate(date);
        }

        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const seconds = date.getUTCSeconds();
        if (hours === 0 && minutes === 0 && seconds === 0) {
          return formatTZDate(date);
        }
        return formatTZDate(date, "UTC", "HH:mm dd/MM/yyyy");
      }
    }

    // Date, Number, Boolean, String
    if (value instanceof Date) return formatTZDate(value, "UTC", "dd/MM/yyyy");
    if (typeof value === "number") return value.toLocaleString("vi-VN");
    if (typeof value === "boolean")
      return value ? (
        <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
      ) : (
        <CloseIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
      );
    if (typeof value === "string") return value;

    // Array
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      if (value.every(v => typeof v === "string")) return value.join(", ");

      // Array of Objects
      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {value.map((item, idx) => {
            if (typeof item === "object" && item !== null) {
              return (
                `${spacing}- ` +
                Object.entries(item)
                  ?.filter(([k]) => k !== "_id")
                  ?.map(([k, v]) => `${k}: ${formatGeneralValue(v, indent + 1)}`)
                  ?.join(", ")
              )};
              return `${spacing}- ${formatGeneralValue(item, indent + 1)}`;
            }).join("\n")
          }
        </Typography>
      );
    }

    // Object
    if (typeof value === "object") {
      const entries = Object.entries(value);
      if (entries.length === 0) return "{}";

      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {entries
            .map(([k, v]) => `${spacing}${k}: ${formatGeneralValue(v, indent + 1)}`)
            .join("\n")}
        </Typography>
      );
    }

    return String(value);
  }, [theme]);
  
  const columns = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const omittedFields = ["_id", "__v", "deletedAt", "deletedBy", "createdAt", "updatedAt", "updatedBy"];
    
    const allKeys = new Set();
    rows.forEach(row => {
      Object.keys(row).forEach(k => allKeys.add(k));
    });
    const dynamicFields = Array.from(allKeys).filter(k => !omittedFields.includes(k));

    return [
      {
        field: "no",
        headerName: "No",
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      ...dynamicFields.map((key) => {
        const maxCellLength = Math.max(
          key.length,
          ...rows?.map(row => row[key] ? String(row[key]).length : 0)
        );

        const specialWidths = {
          // crewKpi: 10,
        };
        const columnWidth = specialWidths[key] ?? Math.min(Math.max(maxCellLength/ 25 * 1, 1.2), 1.5);

        const isNumber = rows.every(
          (row) => row[key] == null ||
            typeof row[key] === "number" ||
            (!isNaN(row[key]) && row[key] !== "")
        );

        return {
          field: key,
          headerName: capitalizeFirstLetter(key),
          editable: false, 
          flex: columnWidth,
          align: isNumber ? "right" : "left",
          renderCell: (params) => (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {formatGeneralValue(params.value)}
            </Box>
          )
        };
      }),      
    ];
  }, [rows, formatGeneralValue]);

  const actionColumn = useMemo(() => {
    if (!canDelete) return [];

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: 80,
        renderCell: (params) => (
          <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: theme.palette.primary.dark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': { color: theme.palette.green.deep },
                }}
                onClick={() => {
                  setDeleteId(params.row._id);
                  setConfirmDialogOpen(true);
                }}
              >
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          </FlexCenter>
        ),
      },
    ];
  }, [canDelete, theme]);
  
  const executeDelete = async () => {
    if (!deleteId) return;
    try {
      await onDelete(deleteId);
    } finally {
      setDeleteId(null);
      setConfirmDialogOpen(false);
    }
  };

  const styles = {
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .kpi-warning": {
      backgroundColor: theme.palette.neutral.light,
      color: theme.palette.error.dark,
      fontWeight: "bold",
    },
  };


  const handleRequestDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  return (
    <Box height="100%">
      {isMobile ? (
        <Box 
          ref={scrollRef} 
          height="100%" 
          sx={{ overflowY: 'auto' }}
        >
          <CrewKPIDataList
            rows={rows}
            onRequestDelete={handleRequestDelete}
          />

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      ) : (
        <DataGridComponent
          rows={rows}
          columns={[...columns, ...actionColumn]}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          props={{
            onProcessRowUpdateError: (error) => console.error(error),
          }}
          sx={styles}
        />
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default CrewKPILogs;
